<template>
  <v-container fluid class="donation-container">
    <!-- Animated Background Elements -->
    <div class="bg-decoration">
      <div class="floating-shape shape-1"></div>
      <div class="floating-shape shape-2"></div>
      <div class="floating-shape shape-3"></div>
    </div>

    <v-row>
      <v-col cols="12">
        <!-- Modern Header Section -->
        <div class="header-section mb-8">
          <v-row align="center" no-gutters>
            <v-col cols="12" lg="8">
              <div class="header-content">
                <div class="header-icon-wrapper">
                  <v-icon size="48" color="white">mdi-hand-heart-outline</v-icon>
                </div>
                <div class="header-text">
                  <h1 class="header-title">
                    Donation History
                  </h1>
                  <p class="header-subtitle">
                    Track and manage all donation activities with advanced analytics
                  </p>
                </div>
              </div>
            </v-col>
            <v-col cols="12" lg="4" class="text-lg-right text-center mt-4 mt-lg-0">
              <v-btn
                color="white"
                large
                elevation="0"
                outlined
                @click="refreshData"
                :loading="loading"
                class="refresh-btn"
              >
                <v-icon left>mdi-refresh</v-icon>
                Refresh Data
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Modern Stats Cards -->
        <v-row class="mb-8">
          <v-col cols="12" sm="6" lg="3" v-for="(stat, index) in stats" :key="index">
            <div class="stats-card-wrapper" :style="{ animationDelay: `${index * 0.1}s` }">
              <v-card 
                class="stats-card glass-effect"
                elevation="0"
                @click="animateCard($event)"
              >
                <div class="stats-card-content">
                  <div class="stats-icon-wrapper" :class="`${stat.color}-gradient`">
                    <v-icon size="28" color="white">{{ stat.icon }}</v-icon>
                  </div>
                  <div class="stats-text">
                    <h3 class="stats-value" :class="`${stat.color}--text`">{{ stat.value }}</h3>
                    <p class="stats-label">{{ stat.label }}</p>
                  </div>
                </div>
                <div class="stats-card-decoration" :class="`${stat.color}-decoration`"></div>
              </v-card>
            </div>
          </v-col>
        </v-row>

                 <!-- BreadCrumb -->
          <div class="glass-breadcrumbs">
            <div class="breadcrumb-nav">
              <div
                class="crumb-item"
                @click="$router.push({ name: 'admin-donations' })"
              >
                <v-icon color="#fff">mdi mdi-currency-usd</v-icon>
                <span>Donations</span>
              </div>
              <div class="crumb-divider">/</div>
              <div class="crumb-item active">
                <v-icon color="#fff">mdi-history</v-icon>
                <span>History</span>
              </div>
            </div>
          </div>

        <!-- Modern Data Table Card -->
        <v-card class="donation-table-card glass-effect" elevation="0">
          <!-- Enhanced Header -->
          <div class="table-header">
            <v-row align="center" no-gutters>
              <v-col cols="12" md="6">
                <div class="table-title-section">
                  <div class="table-icon-wrapper">
                    <v-icon size="24" color="white">mdi-table-large</v-icon>
                  </div>
                  <div>
                    <h2 class="table-title">Donation Records</h2>
                    <p class="table-subtitle">{{ donationDetails.length }} total records</p>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6" class="mt-4 mt-md-0">
                <div class="search-section">
                  <v-text-field
                    v-model="search"
                    placeholder="Search donations..."
                    single-line
                    hide-details
                    outlined
                    dense
                    class="modern-search"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                  >
                    <!-- <template v-slot:append>
                      <v-btn icon small @click="exportData" class="export-btn">
                        <v-icon>mdi-download</v-icon>
                      </v-btn>
                    </template> -->
                  </v-text-field>
                </div>
              </v-col>
            </v-row>
          </div>
          
          <!-- Enhanced Data Table -->
          <div class="table-wrapper">
            <v-data-table
              :headers="headers"
              :items="donationDetails"
              :search="search"
              :loading="loading"
              loading-text="Loading donations..."
              class="modern-table"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [5, 10, 20, 50],
                'items-per-page-text': 'Show:',
                'show-current-page': true,
                'show-first-last-page': true
              }"
              mobile-breakpoint="0"
            >
              <!-- Enhanced Donor Column -->
              <template v-slot:item.donorName="{ item }">
                <div class="donor-cell">
                  <v-avatar size="40" class="donor-avatar">
                    <v-img
                      v-if="item.avatar"
                      :src="item.avatar"
                      :alt="item.donorName"
                    />
                    <span v-else class="avatar-text">
                      {{ item.donorName.charAt(0).toUpperCase() }}
                    </span>
                  </v-avatar>
                  <div class="donor-info">
                    <div class="donor-name">{{ item.donorName }}</div>
                    <div class="donor-email">{{ item.email }}</div>
                  </div>
                </div>
              </template>

              <!-- Enhanced Amount Column -->
              <template v-slot:item.amount="{ item }">
                <div class="amount-cell">
                  <v-chip
                    :color="getAmountColor(item.amount)"
                    text-color="white"
                    small
                    class="amount-chip"
                  >
                    <v-icon left size="14">mdi-currency-usd</v-icon>
                    {{ item.amount }}
                  </v-chip>
                </div>
              </template>

              <!-- Enhanced Commission Column -->
              <template v-slot:item.commission="{ item }">
                <div class="commission-cell">
                  <span class="commission-percentage">{{ item.commission }}%</span>
                </div>
              </template>

              <!-- Enhanced Date Column -->
              <template v-slot:item.date="{ item }">
                <div class="date-cell">
                  <v-icon size="16" color="grey" class="mr-1">mdi-calendar</v-icon>
                  {{ formatDate(item.date) }}
                </div>
              </template>

              <!-- Enhanced Status Column -->
              <template v-slot:item.status="{ item }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  text-color="white"
                  small
                  class="status-chip"
                >
                  <v-icon left size="14">{{ getStatusIcon(item.status) }}</v-icon>
                  {{ item.status }}
                </v-chip>
              </template>

              <!-- Enhanced Actions Column -->
              <template v-slot:item.actions="{ item }">
                <div class="actions-cell">
                  <v-btn
                    icon
                    small
                    color="primary"
                    @click="viewDetails(item)"
                    class="action-btn"
                  >
                    <v-icon size="18">mdi-eye</v-icon>
                  </v-btn>
                </div>
              </template>

              <!-- Enhanced No Data State -->
              <template v-slot:no-data>
                <div class="no-data-state">
                  <div class="no-data-icon">
                    <v-icon size="80" color="grey lighten-3">mdi-database-search</v-icon>
                  </div>
                  <h3 class="no-data-title">No donations found</h3>
                  <p class="no-data-subtitle">Try adjusting your search criteria or check back later</p>
                  <v-btn color="primary" outlined @click="refreshData" class="mt-4">
                    <v-icon left>mdi-refresh</v-icon>
                    Refresh Data
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Enhanced Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="600px" persistent>
      <v-card class="details-dialog glass-effect" elevation="0">
        <div class="dialog-header">
          <div class="dialog-header-content">
            <div class="dialog-icon-wrapper">
              <v-icon size="24" color="white">mdi-information-outline</v-icon>
            </div>
            <h2 class="dialog-title">Donation Details</h2>
          </div>
          <v-btn icon @click="detailsDialog = false" class="close-btn">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </div>
        
        <v-card-text v-if="selectedItem" class="dialog-content mt-2">
          <div class="detail-grid">
            <div class="detail-item" v-for="(detail, index) in dialogDetails" :key="index">
              <div class="detail-icon">
                <v-icon size="20" :color="detail.color">{{ detail.icon }}</v-icon>
              </div>
              <div class="detail-content">
                <div class="detail-label">{{ detail.label }}</div>
                <div class="detail-value">{{ detail.value }}</div>
              </div>
            </div>
          </div>
        </v-card-text>
        
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="detailsDialog = false">
            Cancel
          </v-btn>
          <!-- <v-btn color="primary" @click="downloadReceipt(selectedItem)" class="ml-2">
            <v-icon left>mdi-download</v-icon>
            Download Receipt
          </v-btn> -->
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'AdminDonationsDetails',
  data() {
    return {
      search: '',
      loading: false,
      detailsDialog: false,
      selectedItem: null,
      headers: [
        { text: 'Donor', value: 'donorName', sortable: true, width: '25%' },
        { text: 'Amount', value: 'amount', sortable: true, align: 'center', width: '15%' },
        { text: 'Commission', value: 'commission', sortable: true, align: 'center', width: '12%' },
        { text: 'Date', value: 'date', sortable: true, align: 'center', width: '15%' },
        { text: 'Status', value: 'status', sortable: true, align: 'center', width: '12%' },
        { text: 'Actions', value: 'actions', sortable: false, align: 'center', width: '15%' }
      ]
    };
  },
  computed: {
    ...mapGetters({
      allAgencyDonations: 'donation/getAllAgencyDonations'
    }),
    donationDetails() {
      const raw = Array.isArray(this.allAgencyDonations?.res)
        ? this.allAgencyDonations.res
        : Array.isArray(this.allAgencyDonations)
          ? this.allAgencyDonations
          : [];
      return raw.map(item => ({
        donorName: item.payer?.name || 'Unknown',
        email: item.payer?.email || 'N/A',
        avatar: item.payer?.midThumbnail || null,
        amount: this.formatCents(item.pay_in ?? 0),
        commission: item.commision_percentage ?? 0,
        paidAmount: this.formatCents((item.pay_in ?? 0) - ((item.commision_percentage ?? 0) / 100 * (item.pay_in ?? 0))),
        date: item.date || '',
        paidDate: item.paid_date || '',
        status: item.status === 'Paid' ? 'Completed' : (item.status || 'Unknown')
      }));
    },
    stats() {
      return [
        {
          icon: 'mdi-cash-multiple',
          value: this.totalAmount,
          label: 'Total Raised',
          color: 'success'
        },
        {
          icon: 'mdi-account-heart',
          value: this.donationDetails.length,
          label: 'Total Donors',
          color: 'info'
        },
        {
          icon: 'mdi-clock-outline',
          value: this.pendingCount,
          label: 'Pending',
          color: 'warning'
        },
        {
          icon: 'mdi-check-circle',
          value: this.completedCount,
          label: 'Completed',
          color: 'success'
        }
      ];
    },
    totalAmount() {
      return this.donationDetails.reduce((total, item) => {
        const amount = parseFloat(item.amount.replace(/[^0-9.-]+/g, ''));
        return total + amount;
      }, 0).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    pendingCount() {
      return this.donationDetails.filter(item => item.status === 'Pending').length;
    },
    completedCount() {
      return this.donationDetails.filter(item => item.status === 'Completed').length;
    },
    dialogDetails() {
      if (!this.selectedItem) return [];
      return [
        {
          icon: 'mdi-account',
          label: 'Donor Name',
          value: this.selectedItem.donorName,
          color: 'primary'
        },
        {
          icon: 'mdi-email',
          label: 'Email',
          value: this.selectedItem.email,
          color: 'info'
        },
        {
          icon: 'mdi-currency-usd',
          label: 'Amount',
          value: this.selectedItem.amount,
          color: 'success'
        },
        {
          icon: 'mdi-percent',
          label: 'Commission',
          value: `${this.selectedItem.commission}%`,
          color: 'orange'
        },
        {
          icon: 'mdi-calendar',
          label: 'Date',
          value: this.formatDate(this.selectedItem.date),
          color: 'purple'
        },
        {
          icon: 'mdi-information',
          label: 'Status',
          value: this.selectedItem.status,
          color: this.getStatusColor(this.selectedItem.status)
        }
      ];
    }
  },
  created() {
    this.fetchDonationDetails();
  },
  methods: {
    ...mapActions({
      fetchAllDonations: 'donation/agencydetailedPayments'
    }),
    async fetchDonationDetails() {
      this.loading = true;
      try {
        const agencyId = this.$route.params.id;
        const payload = { agency_id: agencyId };
        await this.fetchAllDonations(payload);
      } catch (error) {
        console.error('Error fetching donation details:', error);
      } finally {
        this.loading = false;
      }
    },
    refreshData() {
      this.fetchDonationDetails();
    },
    getStatusColor(status) {
      switch (status) {
        case 'Completed': return 'success';
        case 'Pending': return 'warning';
        case 'Failed': return 'error';
        default: return 'grey';
      }
    },
    getStatusIcon(status) {
      switch (status) {
        case 'Completed': return 'mdi-check-circle';
        case 'Pending': return 'mdi-clock-outline';
        case 'Failed': return 'mdi-close-circle';
        default: return 'mdi-help-circle';
      }
    },
    getAmountColor(amount) {
      const value = parseFloat(amount.replace(/[$,]/g, ''));
      if (value >= 1000) return 'success';
      if (value >= 500) return 'info';
      return 'primary';
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },
    formatCents(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount / 100);
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    viewDetails(item) {
      this.selectedItem = item;
      this.detailsDialog = true;
    },
    downloadReceipt(item) {
      // Implement download functionality
      console.log('Download receipt for:', item);
    },
    exportData() {
      // Implement export functionality
      console.log('Export data');
    },
    animateCard(event) {
      const card = event.currentTarget;
      card.style.transform = 'scale(0.95)';
      setTimeout(() => {
        card.style.transform = 'scale(1)';
      }, 150);
    }
  }
}
</script>

<style scoped>
/* Container and Background */
.donation-container {
  /* background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); */
  min-height: 100vh;
  padding: 20px;
  position: relative;
  overflow: hidden;
}

.bg-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
}

.floating-shape {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float 6s ease-in-out infinite;
}

.shape-1 {
  width: 100px;
  height: 100px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 150px;
  height: 150px;
  top: 20%;
  right: 10%;
  animation-delay: 2s;
}

.shape-3 {
  width: 80px;
  height: 80px;
  bottom: 10%;
  left: 20%;
  animation-delay: 4s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

/* Glass Effect */
.glass-effect {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

/* Header Section */
.header-section {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 32px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 2;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 24px;
}

.header-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  padding: 16px;
  backdrop-filter: blur(10px);
}

.header-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  margin: 8px 0 0 0;
  font-weight: 300;
}

.refresh-btn {
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  color: white !important;
  transition: all 0.3s ease;
}

.refresh-btn:hover {
  background: white !important;
  color: #084468 !important;
  transform: translateY(-2px);
}

/* Stats Cards */
.stats-card-wrapper {
  animation: slideUp 0.6s ease-out forwards;
  opacity: 0;
  transform: translateY(30px);
  position: relative;
  z-index: 2;
}

@keyframes slideUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-card {
  border-radius: 20px;
  padding: 24px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.stats-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.stats-card-content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 2;
}

.stats-icon-wrapper {
  border-radius: 16px;
  padding: 16px;
  min-width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.success-gradient {
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
}

.info-gradient {
  background: linear-gradient(135deg, #2196f3 0%, #1565c0 100%);
}

.warning-gradient {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.stats-value {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.stats-label {
  font-size: 0.875rem;
  color: #666;
  margin: 4px 0 0 0;
  font-weight: 500;
}

.stats-card-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  opacity: 0.1;
  z-index: 1;
}

.success-decoration {
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
}

.info-decoration {
  background: linear-gradient(135deg, #2196f3 0%, #1565c0 100%);
}

.warning-decoration {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

/* Table Card */
.donation-table-card {
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  z-index: 2;
}

.table-header {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  padding: 24px;
  color: white;
}

.table-title-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.table-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.table-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

.table-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  margin: 4px 0 0 0;
}

.search-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modern-search >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 12px !important;
}

.modern-search >>> .v-input__slot:hover {
  background: rgba(255, 255, 255, 0.15) !important;
}

.modern-search >>> .v-text-field__slot input {
  color: white !important;
}

.modern-search >>> .v-text-field__slot input::placeholder {
  color: rgba(255, 255, 255, 0.7) !important;
}

.modern-search >>> .v-icon {
  color: rgba(255, 255, 255, 0.8) !important;
}

.export-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: white !important;
}

/* Table Styling */
.table-wrapper {
  background: rgba(255, 255, 255, 0.95);
}

.modern-table >>> .v-data-table__wrapper {
  background: transparent;
}

.modern-table >>> thead tr {
  background: #f8fafc;
}

.modern-table >>> thead th {
  color: #4a5568 !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  padding: 16px !important;
  border-bottom: 2px solid #e2e8f0 !important;
}

.modern-table >>> tbody tr {
  transition: all 0.2s ease;
}

.modern-table >>> tbody tr:hover {
  background: rgba(102, 126, 234, 0.05) !important;
  transform: translateX(2px);
}

.modern-table >>> tbody td {
  padding: 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

/* Cell Styling */
.donor-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.donor-avatar {
  background: linear-gradient(135deg, #084468 0%, #3b7ca1 100%);
  border: 2px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.avatar-text {
  color: white;
  font-weight: 600;
  font-size: 1rem;
}

.donor-info {
  flex: 1;
  min-width: 0;
}

.donor-name {
  font-weight: 600;
  color: #2d3748;
  font-size: 0.875rem;
}

.donor-email {
  color: #718096;
  font-size: 0.75rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.amount-chip {
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.commission-cell {
  text-align: center;
}

.commission-percentage {
  background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
  color: white;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 600;
}

.date-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 0.875rem;
  color: #4a5568;
}

.status-chip {
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.actions-cell {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.action-btn {
  background: rgba(102, 126, 234, 0.1) !important;
  transition: all 0.2s ease;
}

.action-btn:hover {
  background: rgba(102, 126, 234, 0.2) !important;
  transform: scale(1.1);
}

/* No Data State */
.no-data-state {
  padding: 60px 20px;
  text-align: center;
}

.no-data-icon {
  margin-bottom: 24px;
}

.no-data-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #4a5568;
  margin: 0 0 12px 0;
}

.no-data-subtitle {
  color: #718096;
  font-size: 1rem;
  margin: 0 0 24px 0;
}

/* Dialog Styling */
.details-dialog {
  border-radius: 20px;
  overflow: hidden;
  max-width: 600px;
  width: 100%;
}

.dialog-header {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dialog-header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.dialog-icon-wrapper {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
  backdrop-filter: blur(10px);
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
  margin: 0;
}

.close-btn {
  background: rgba(255, 255, 255, 0.1) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.dialog-content {
  padding: 32px;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 4px solid #084468;
  transition: all 0.2s ease;
}

.detail-item:hover {
  background: #f1f5f9;
  transform: translateX(4px);
}

.detail-icon {
  background: white;
  border-radius: 8px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.detail-content {
  flex: 1;
}

.detail-label {
  font-size: 0.875rem;
  color: #718096;
  font-weight: 500;
  margin-bottom: 4px;
}

.detail-value {
  font-size: 1rem;
  color: #2d3748;
  font-weight: 600;
}

.dialog-actions {
  padding: 24px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
}

/* Footer Styling */
.modern-table >>> .v-data-footer {
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  padding: 16px 24px;
}

.modern-table >>> .v-data-footer__select {
  margin: 0 16px;
}

.modern-table >>> .v-data-footer__pagination {
  margin: 0 16px;
}

/* Responsive Design */
@media (max-width: 1264px) {
  .header-content {
    flex-direction: column;
    text-align: center;
    gap: 16px;
  }
  
  .header-title {
    font-size: 2rem;
  }
  
  .table-title-section {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
}

@media (max-width: 960px) {
  .donation-container {
    padding: 16px;
  }
  
  .header-section {
    padding: 24px;
  }
  
  .header-title {
    font-size: 1.8rem;
  }
  
  .stats-card {
    padding: 20px;
  }
  
  .stats-card-content {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
  
  .stats-icon-wrapper {
    align-self: center;
  }
  
  .table-header {
    padding: 20px;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
  
  .detail-item {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }
}

@media (max-width: 600px) {
  .donation-container {
    padding: 12px;
  }
  
  .header-section {
    padding: 20px;
  }
  
  .header-title {
    font-size: 1.5rem;
  }
  
  .header-subtitle {
    font-size: 1rem;
  }
  
  .stats-card {
    padding: 16px;
  }
  
  .stats-value {
    font-size: 1.5rem;
  }
  
  .table-header {
    padding: 16px;
  }
  
  .table-title {
    font-size: 1.25rem;
  }
  
  .modern-table >>> tbody td {
    padding: 12px 8px !important;
  }
  
  .donor-cell {
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
  
  .donor-name {
    font-size: 0.8rem;
  }
  
  .donor-email {
    font-size: 0.7rem;
  }
  
  .actions-cell {
    flex-direction: column;
    gap: 4px;
  }
  
  .dialog-content {
    padding: 20px;
  }
  
  .detail-item {
    padding: 12px;
  }
  
  .no-data-state {
    padding: 40px 16px;
  }
  
  .no-data-title {
    font-size: 1.25rem;
  }
}

/* Animation Classes */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

/* Loading Animation */
.modern-table >>> .v-data-table__progress .v-progress-linear {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
}

/* Scrollbar Styling */
.table-wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg,  #084468 0%, #3b7ca1 100%);
}

/* Focus States */
.modern-search >>> .v-input__slot.v-input__slot--focused {
  background: rgba(255, 255, 255, 0.2) !important;
  border: 1px solid rgba(255, 255, 255, 0.5) !important;
}

.action-btn:focus {
  outline: 2px solid rgba(102, 126, 234, 0.5);
  outline-offset: 2px;
}

/* Accessibility */
.stats-card:focus-visible {
  outline: 2px solid #084468;
  outline-offset: 2px;
}

.donor-avatar:focus-visible {
  outline: 2px solid #084468;
  outline-offset: 2px;
}

/* Print Styles */
@media print {
  .donation-container {
    background: white;
    color: black;
  }
  
  .header-section,
  .stats-card,
  .donation-table-card {
    background: white;
    box-shadow: none;
    border: 1px solid #ccc;
  }
  
  .floating-shape {
    display: none;
  }
  
  .actions-cell {
    display: none;
  }
}

/* Dark Mode Support */
/* @media (prefers-color-scheme: dark) {
  .glass-effect {
    background: rgba(30, 41, 59, 0.95) !important;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .detail-item {
    background: rgba(30, 41, 59, 0.5);
    border-left-color: #667eea;
  }
  
  .detail-item:hover {
    background: rgba(30, 41, 59, 0.7);
  }
  
  .detail-label {
    color: #94a3b8;
  }
  
  .detail-value {
    color: #f1f5f9;
  }
  
  .donor-name {
    color: #f1f5f9;
  }
  
  .donor-email {
    color: #94a3b8;
  }
  
  .no-data-title {
    color: #f1f5f9;
  }
  
  .no-data-subtitle {
    color: #94a3b8;
  }
} */

/* High Contrast Mode */
@media (prefers-contrast: high) {
  .glass-effect {
    background: white !important;
    border: 2px solid #000;
  }
  
  .stats-card {
    border: 2px solid #000;
  }
  
  .amount-chip,
  .status-chip {
    border: 1px solid #000;
  }
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  .stats-card,
  .action-btn,
  .detail-item {
    transition: none;
  }
  
  .floating-shape {
    animation: none;
  }
  
  .stats-card-wrapper {
    animation: none;
    opacity: 1;
    transform: none;
  }
  
  .stats-card:hover {
    transform: none;
  }
  
  .action-btn:hover {
    transform: none;
  }
}

/* BreadCrumb */

.glass-breadcrumbs {
  margin: 14px 0 17px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(12px);
  border-radius: 50px;
  padding: 4px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  width: fit-content;
}

.crumb-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 20px;
  border-radius: 50px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  color: rgba(255, 255, 255, 0.8);
}

.crumb-item:hover {
  background: rgba(255, 255, 255, 0.15);
  color: white;
}

.crumb-item.active {
  background: linear-gradient(135deg, #084468, #0a5b8a);
  color: white;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.crumb-divider {
  color: rgba(255, 255, 255, 0.3);
  padding: 0 4px;
  user-select: none;
}

@media (max-width: 600px) {
  .crumb-item span {
    display: none;
  }
  .crumb-item {
    padding: 8px 12px;
  }
}
</style>